import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import ProtectedLink from "../../components/ProtectedLink";

const STATUS_CONFIG = {
  to_prepare: { label: "À préparer",       icon: "🟡", color: "#d97706", bg: "#fffbeb" },
  in_progress:{ label: "En cours",          icon: "🔵", color: "#2563eb", bg: "#eff6ff" },
  ready:      { label: "Prêt à expédier",   icon: "🟢", color: "#16a34a", bg: "#f0fdf4" },
  shipped:    { label: "Expédié",           icon: "✅", color: "#6b7280", bg: "#f9fafb" },
  cancelled:  { label: "Annulée",           icon: "🚫", color: "#dc2626", bg: "#fef2f2" },
};
const CARRIERS = ["DHL", "Colissimo", "DPD", "FedEx", "Manuel"];

export default function PreparationTab({ currentCompanyId, products = [], onRefresh }) {
  const [preps, setPreps]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [expanded, setExpanded]       = useState(null);
  const [showCreate, setShowCreate]   = useState(false);
  const [showShipModal, setShowShipModal] = useState(null);
  const [trackingInput, setTrackingInput] = useState("");
  const [carrierInput, setCarrierInput]   = useState("Colissimo");
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");

  // Formulaire création
  const [newForm, setNewForm] = useState({ order_ref: "", client_name: "", notes: "" });
  const [newLines, setNewLines] = useState([{ product_id: "", product_name: "", product_sku: "", quantity_expected: "1", unit: "pièce", warehouse_location: "" }]);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.stocks.preparations(currentCompanyId, filterStatus ? { status: filterStatus } : {})
      .then(data => setPreps(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (prepId, status) => {
    if (status === "shipped") { setShowShipModal(prepId); setTrackingInput(""); return; }
    setSaving(true);
    try { await api.stocks.preparationStatus(prepId, { status }); load(); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const confirmShip = async () => {
    if (!showShipModal) return;
    setSaving(true);
    try {
      await api.stocks.preparationStatus(showShipModal, {
        status: "shipped", carrier: carrierInput, tracking_number: trackingInput,
      });
      setShowShipModal(null); load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handlePick = async (prep, line) => {
    const newPicked = !line.is_picked;
    try {
      await api.stocks.pickLine(prep.id, line.id, { is_picked: newPicked, quantity_picked: newPicked ? line.quantity_expected : 0 });
      load();
    } catch (e) { setError(e.message); }
  };

  const handleCancel = async (prepId) => {
    if (!window.confirm("Annuler cette préparation ? La réservation de stock sera libérée immédiatement.")) return;
    setSaving(true);
    try { await api.stocks.cancelPreparation(prepId); load(); }
    catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      const lines = newLines.filter(l => l.product_name || l.product_id).map(l => ({
        product_id: l.product_id || null,
        product_name: l.product_name,
        product_sku: l.product_sku || null,
        unit: l.unit,
        quantity_expected: parseFloat(l.quantity_expected) || 1,
        warehouse_location: l.warehouse_location || null,
      }));
      await api.stocks.createPreparation({ company_id: currentCompanyId, ...newForm, lines });
      setShowCreate(false);
      setNewForm({ order_ref: "", client_name: "", notes: "" });
      setNewLines([{ product_id: "", product_name: "", product_sku: "", quantity_expected: "1", unit: "pièce", warehouse_location: "" }]);
      load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const addLine = () => setNewLines(l => [...l, { product_id: "", product_name: "", product_sku: "", quantity_expected: "1", unit: "pièce", warehouse_location: "" }]);
  const removeLine = (i) => setNewLines(l => l.filter((_, idx) => idx !== i));
  const setLineProduct = (i, product) => {
    setNewLines(l => l.map((line, idx) => idx === i
      ? { ...line, product_id: String(product.id), product_name: product.name, product_sku: product.sku, unit: product.unit }
      : line
    ));
  };

  const counts = Object.fromEntries(Object.keys(STATUS_CONFIG).map(s => [s, preps.filter(p => p.status === s).length]));

  const filtered = filterStatus ? preps.filter(p => p.status === filterStatus) : preps;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontWeight: 700 }}>Préparation de commandes</h3>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>Vue logisticien — gestion du picking et expédition</div>
        </div>
        <button className="btn primary" onClick={() => setShowCreate(true)}>+ Nouvelle préparation</button>
      </div>

      {error && <div className="alert danger" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Filtres statuts */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={() => setFilterStatus("")} className="btn" style={{ fontWeight: !filterStatus ? 700 : 400, borderColor: !filterStatus ? "var(--g)" : undefined }}>
          Toutes ({preps.length})
        </button>
        {Object.entries(STATUS_CONFIG).map(([s, cfg]) => (
          <button key={s} onClick={() => setFilterStatus(s)} className="btn"
            style={{ background: filterStatus === s ? cfg.bg : undefined, color: filterStatus === s ? cfg.color : undefined, borderColor: filterStatus === s ? cfg.color : undefined, fontWeight: filterStatus === s ? 700 : 400 }}>
            {cfg.icon} {cfg.label} ({counts[s] || 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          Aucune préparation{filterStatus ? ` "${STATUS_CONFIG[filterStatus]?.label}"` : ""}.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map(prep => {
            const cfg = STATUS_CONFIG[prep.status] || STATUS_CONFIG.to_prepare;
            const totalLines = prep.lines?.length || 0;
            const pickedLines = prep.lines?.filter(l => l.is_picked).length || 0;
            const isExpanded = expanded === prep.id;

            return (
              <div key={prep.id} className="card" style={{ border: `1.5px solid ${cfg.color}33`, padding: 0, overflow: "hidden" }}>
                {/* En-tête préparation */}
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer", background: cfg.bg }}
                  onClick={() => setExpanded(isExpanded ? null : prep.id)}>
                  <span style={{ fontSize: 20 }}>{cfg.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                      {prep.order_ref || `PREP-${prep.id}`}
                      {prep.client_name && <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 8 }}>— {prep.client_name}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: cfg.color, fontWeight: 600 }}>{cfg.label}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right" }}>
                    <div>Picking : {pickedLines}/{totalLines} lignes</div>
                    {prep.logisticien_name && <div>👤 {prep.logisticien_name}</div>}
                    {prep.carrier && <div>🚚 {prep.carrier}{prep.tracking_number ? ` · ${prep.tracking_number}` : ""}</div>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{new Date(prep.created_at).toLocaleDateString("fr-FR")}</div>
                  <span style={{ color: "var(--text-muted)", fontSize: 12 }}>{isExpanded ? "▲" : "▼"}</span>
                </div>

                {/* Détail picking */}
                {isExpanded && (
                  <div style={{ padding: "0 16px 16px" }}>
                    {/* Progress bar */}
                    {totalLines > 0 && (
                      <div style={{ margin: "12px 0 8px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                          <span>Progression picking</span>
                          <span style={{ fontWeight: 700 }}>{Math.round(pickedLines / totalLines * 100)}%</span>
                        </div>
                        <div style={{ background: "#e5e7eb", borderRadius: 4, height: 6 }}>
                          <div style={{ background: cfg.color, height: 6, borderRadius: 4, width: `${pickedLines / totalLines * 100}%`, transition: "width 0.3s" }} />
                        </div>
                      </div>
                    )}

                    {/* Lignes picking */}
                    <div className="tbl-overflow" style={{ marginBottom: 12 }}>
                      <table className="tbl" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th style={{ width: 36 }}>✓</th>
                            <th>Produit</th>
                            <th>SKU</th>
                            <th>Emplacement</th>
                            <th>Qté attendue</th>
                            <th>Qté préparée</th>
                          </tr>
                        </thead>
                        <tbody>
                          {prep.lines?.length === 0 && (
                            <tr><td colSpan={6} style={{ textAlign: "center", color: "var(--text-muted)" }}>Aucune ligne</td></tr>
                          )}
                          {prep.lines?.map(line => (
                            <tr key={line.id} style={{ background: line.is_picked ? "#f0fdf4" : "inherit", opacity: prep.status === "shipped" ? 0.7 : 1 }}>
                              <td>
                                <input type="checkbox" checked={!!line.is_picked}
                                  disabled={prep.status === "shipped"}
                                  onChange={() => handlePick(prep, line)} />
                              </td>
                              <td style={{ fontWeight: line.is_picked ? 400 : 600, textDecoration: line.is_picked ? "line-through" : "none", color: line.is_picked ? "var(--text-muted)" : "inherit" }}>
                                {line.product_name}
                              </td>
                              <td style={{ fontFamily: "monospace", fontSize: 11 }}>{line.product_sku || "—"}</td>
                              <td style={{ color: "var(--text-muted)" }}>{line.warehouse_location || "—"}</td>
                              <td style={{ fontWeight: 700 }}>{line.quantity_expected} {line.unit}</td>
                              <td style={{ color: line.is_picked ? "#16a34a" : "var(--text-muted)" }}>
                                {line.is_picked ? line.quantity_picked : "—"}
                                {line.is_picked && line.picked_by && <div style={{ fontSize: 10, color: "var(--text-muted)" }}>par {line.picked_by}</div>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Actions */}
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      {prep.status === "to_prepare" && (
                        <button className="btn primary" onClick={() => handleStatusChange(prep.id, "in_progress")} disabled={saving}>
                          🔵 Commencer la préparation
                        </button>
                      )}
                      {prep.status === "in_progress" && (
                        <button className="btn primary" onClick={() => handleStatusChange(prep.id, "ready")}
                          disabled={saving || pickedLines < totalLines}
                          title={pickedLines < totalLines ? "Toutes les lignes doivent être cochées" : ""}>
                          🟢 Marquer comme prêt
                        </button>
                      )}
                      {prep.status === "ready" && (
                        <button className="btn primary" style={{ background: "#6b7280" }} onClick={() => handleStatusChange(prep.id, "shipped")} disabled={saving}>
                          ✅ Confirmer l'expédition
                        </button>
                      )}
                      {["to_prepare", "in_progress", "ready"].includes(prep.status) && (
                        <button className="btn" style={{ color: "#dc2626" }} onClick={() => handleCancel(prep.id)} disabled={saving}
                          title="Libère immédiatement la réservation de stock — le stock physique n'est jamais touché.">
                          🚫 Annuler la préparation
                        </button>
                      )}
                      <ProtectedLink newTab url={api.stocks.shippingLabel(prep.id)}
                        style={{ textDecoration: "none" }}
                      >
                        <button className="btn">📄 Bordereau PDF</button>
                      </ProtectedLink>
                      {prep.notes && (
                        <span style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic" }}>📝 {prep.notes}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal confirmation expédition */}
      {showShipModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowShipModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3 className="modal-title">✅ Confirmer l'expédition</h3>
              <button className="modal-close" onClick={() => setShowShipModal(null)}>✕</button>
            </div>
            <div className="form-group">
              <label className="form-label">Transporteur</label>
              <select className="form-control" value={carrierInput} onChange={e => setCarrierInput(e.target.value)}>
                {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">N° de suivi transporteur</label>
              <input className="form-control" placeholder="ex: 1Z999AA10123456784" value={trackingInput} onChange={e => setTrackingInput(e.target.value)} />
            </div>
            <div className="btn-group" style={{ justifyContent: "flex-end", marginTop: 8 }}>
              <button className="btn" onClick={() => setShowShipModal(null)}>Annuler</button>
              <button className="btn primary" onClick={confirmShip} disabled={saving}>
                {saving ? "Envoi…" : "Confirmer expédition"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal nouvelle préparation */}
      {showCreate && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="modal" style={{ maxWidth: 680, maxHeight: "90vh", overflowY: "auto" }}>
            <div className="modal-header">
              <h3 className="modal-title">📋 Nouvelle préparation</h3>
              <button className="modal-close" onClick={() => setShowCreate(false)}>✕</button>
            </div>
            {error && <div className="alert danger" style={{ marginBottom: 12 }}>{error}</div>}
            <form onSubmit={handleCreate}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Référence commande</label>
                  <input className="form-control" placeholder="CMD-2024-001" value={newForm.order_ref} onChange={e => setNewForm(f => ({ ...f, order_ref: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Client</label>
                  <input className="form-control" placeholder="Nom du client" value={newForm.client_name} onChange={e => setNewForm(f => ({ ...f, client_name: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <input className="form-control" placeholder="Instructions spéciales…" value={newForm.notes} onChange={e => setNewForm(f => ({ ...f, notes: e.target.value }))} />
              </div>

              <div style={{ borderTop: "1px solid var(--border-color)", margin: "12px 0 8px", paddingTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>Lignes de picking ({newLines.length})</span>
                  <button type="button" className="btn sm" onClick={addLine}>+ Ajouter ligne</button>
                </div>
                {newLines.map((line, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8, padding: 10, background: "#f9fafb", borderRadius: 8 }}>
                    <div style={{ flex: 2 }}>
                      <select className="form-control" style={{ fontSize: 12, marginBottom: 4 }}
                        value={line.product_id}
                        onChange={e => {
                          const p = products.find(p => String(p.id) === e.target.value);
                          if (p) setLineProduct(i, p);
                          else setNewLines(l => l.map((x, idx) => idx === i ? { ...x, product_id: e.target.value, product_name: "", product_sku: "" } : x));
                        }}>
                        <option value="">— Sélectionner produit —</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                      </select>
                      {!line.product_id && (
                        <input className="form-control" style={{ fontSize: 12 }} placeholder="Ou saisir nom produit"
                          value={line.product_name} onChange={e => setNewLines(l => l.map((x, idx) => idx === i ? { ...x, product_name: e.target.value } : x))} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <input className="form-control" type="number" min="0.01" step="0.01" placeholder="Qté" style={{ fontSize: 12 }}
                        value={line.quantity_expected} onChange={e => setNewLines(l => l.map((x, idx) => idx === i ? { ...x, quantity_expected: e.target.value } : x))} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <input className="form-control" placeholder="Emplacement" style={{ fontSize: 12 }}
                        value={line.warehouse_location} onChange={e => setNewLines(l => l.map((x, idx) => idx === i ? { ...x, warehouse_location: e.target.value } : x))} />
                    </div>
                    <button type="button" className="btn sm" style={{ color: "var(--red)", marginTop: 2 }} onClick={() => removeLine(i)} disabled={newLines.length === 1}>✕</button>
                  </div>
                ))}
              </div>

              <div className="btn-group" style={{ justifyContent: "flex-end", marginTop: 8 }}>
                <button type="button" className="btn" onClick={() => setShowCreate(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving}>{saving ? "Création…" : "Créer la préparation"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
